import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import buildInfo from '../../../build-info.json';
import { FiChevronRight } from 'react-icons/fi';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { ReactQueryKeys } from 'constants/react-query-keys';
import HelpService from 'services/HelpService';
import toast from 'react-hot-toast';
import axios from 'axios';

/**
 * @description Help component displays application information, version details, and support resources.
 * This component fetches help information from the backend and displays application name, version,
 * product details (PN, UDI), copyright information, and links to user manual, support, privacy policy,
 * and license agreement.
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 08-12-2026 aleksandar dukleski: NO-TICKET read the FE version from the CI-generated
 * build-info.json instead of package.json, and consume the backend's new flat
 * /app-version shape
 *
 * @returns {JSX.Element} The rendered Help component
 */
export const Help = () => {
  const { t } = useTranslation();
  const today = dayjs();

  /**
   * @description Fetches help information from the backend service
   * Includes version information and other help-related data
   */
  const { isLoading, isFetching, data, failureReason } = useQuery({
    queryKey: [ReactQueryKeys.HELP],
    queryFn: HelpService.getHelpInfo,
  });

  // Display an error toast if the help information fetch fails. Run it from an
  // effect, not during render — calling toast.error mid-render updates the Toaster
  // while Help is still rendering. failureReason is the actual error object.
  useEffect(() => {
    if (failureReason) {
      toast.error(
        axios.isAxiosError(failureReason)
          ? failureReason.response?.data?.message
          : t('errors.default'),
      );
    }
  }, [failureReason, t]);

  return (
    <>
      <h4 className="pb-5">{t('help.title')} </h4>
      <div className="main-container with-tabs">
        <ul className="list-unstyled d-flex flex-column gap-3">
          <li className="d-flex">
            <b className="flex-fill pe-3">{t('help.name')}</b>
            {(isLoading || isFetching) && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
          </li>
          <li>
            {t('help.version')} FE {buildInfo.version} / BE{' '}
            {data?.data?.version}
          </li>
          <li>&nbsp;</li>
          <li>PN 10003</li>
          <li>{t('help.UDI')} 1234567890</li>
          <li>
            {t('help.copyright')} {today.format('YYYY')}
          </li>
          <li>
            <a
              href="https://druglibrary-public-docs.s3.us-east-2.amazonaws.com/AMGIS+Drug+Library+User+Manual.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none text-reset help-link"
            >
              <span> {t('help.userManual')}</span>
              <FiChevronRight size={26} />
            </a>
          </li>
          {/* <li>
            <a
              href="#"
              className="text-decoration-none text-reset help-link"
            >
              <span>{t('help.faq')}</span> <FiChevronRight size={26} />
            </a>
          </li> */}
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="mailto:techsupport@amgis.us?subject=Amgis Technical Support"
              className="text-decoration-none text-reset help-link"
            >
              <span>{t('help.contactSupport')}</span>
              <FiChevronRight size={26} />
            </a>
          </li>
          <li>
            <a
              href="https://www.termsfeed.com/live/54e2c6e1-1d51-4c8c-9a52-e81a8efcccf2"
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none text-reset help-link"
            >
              <span> {t('help.privacyPolicy')}</span>
              <FiChevronRight size={30} />
            </a>
          </li>
          <li>
            <a
              href="https://druglibrary-public-docs.s3.us-east-2.amazonaws.com/Drug+Library+Application+EULA.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none text-reset help-link"
            >
              <span>{t('help.licenseAgreement')} </span>
              <FiChevronRight size={26} />
            </a>
          </li>
        </ul>
      </div>
    </>
  );
};
